import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Grid,
} from '@mui/material';
import exerciseContainerService from '../services/exerciseContainerService';

interface PatternInstanceModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (count: number, containerId: number | null) => void;
  patternTitle: string;
}

interface ExerciseContainer {
  id: number;
  title: string;
  parent?: number | null;
}

const PatternInstanceModal: React.FC<PatternInstanceModalProps> = ({
  open,
  onClose,
  onConfirm,
  patternTitle,
}) => {
  const [selectedCount, setSelectedCount] = useState<number | null>(null);
  const [containers, setContainers] = useState<ExerciseContainer[]>([]);
  const [selectedContainer, setSelectedContainer] = useState<ExerciseContainer | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadContainers();
      // Reset selections when modal opens
      setSelectedCount(null);
      setSelectedContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      // Load all containers recursively
      const allContainers = await loadAllContainersRecursively();
      setContainers(allContainers);

      // Check if default container exists
      const defaultContainer = allContainers.find(
        (c) => c.title === 'Exercices générés à la volée pour peupler une séance'
      );
      if (defaultContainer) {
        setSelectedContainer(defaultContainer);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllContainersRecursively = async (): Promise<ExerciseContainer[]> => {
    const allContainers: ExerciseContainer[] = [];
    const visited = new Set<number>();

    const loadLevel = async (parentId: number | null) => {
      const containers = await exerciseContainerService.getContainers(parentId);
      for (const container of containers) {
        if (!visited.has(container.id)) {
          visited.add(container.id);
          allContainers.push(container);
          // Recursively load children
          await loadLevel(container.id);
        }
      }
    };

    await loadLevel(null);
    return allContainers;
  };

  const handleConfirm = () => {
    if (selectedCount !== null) {
      onConfirm(selectedCount, selectedContainer?.id || null);
      onClose();
    }
  };

  const numberButtons = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Créer des exercices à partir du pattern</Typography>
        <Typography variant="body2" color="text.secondary">
          {patternTitle}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Nombre d'exercices à créer
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {numberButtons.map((num) => (
              <Grid item xs={2.4} key={num}>
                <Button
                  variant={selectedCount === num ? 'contained' : 'outlined'}
                  color="primary"
                  fullWidth
                  sx={{
                    height: 80,
                    fontSize: '2rem',
                    fontWeight: 'bold',
                  }}
                  onClick={() => setSelectedCount(num)}
                >
                  {num}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Container de destination
            </Typography>
            <Autocomplete
              options={containers}
              getOptionLabel={(option) => option.title}
              value={selectedContainer}
              onChange={(_, newValue) => setSelectedContainer(newValue)}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sélectionner un container"
                  placeholder="Exercices générés à la volée pour peupler une séance"
                  helperText="Par défaut : 'Exercices générés à la volée pour peupler une séance' (sera créé si nécessaire)"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={selectedCount === null}
        >
          Créer {selectedCount ? `${selectedCount} exercice${selectedCount > 1 ? 's' : ''}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatternInstanceModal;
